'use client';
import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import Badge from '@/components/ui/Badge';

// BACKEND INTEGRATION: GET /api/check-ins?limit=8&userId=me
const pastEntries = [
  {
    id: 'entry-aug20',
    date: '20 Aug',
    weekday: 'Wednesday',
    mood: 4,
    moodLabel: 'Good',
    urge: 2,
    triggers: ['Work stress'],
    excerpt: 'Had a long day at work but managed to go for a walk instead of opening my usual sites. Felt good afterwards.',
  },
  {
    id: 'entry-aug19',
    date: '19 Aug',
    weekday: 'Tuesday',
    mood: 5,
    moodLabel: 'Great',
    urge: 2,
    triggers: [],
    excerpt: 'Really positive day. Met a friend for coffee. No urges at all — noticed how much better I felt being social.',
  },
  {
    id: 'entry-aug18',
    date: '18 Aug',
    weekday: 'Monday',
    mood: 3,
    moodLabel: 'Okay',
    urge: 5,
    triggers: ['Boredom', 'Evening restlessness'],
    excerpt: 'Monday evening is always the hardest. Did the 5-minute breathing exercise and it helped a bit.',
  },
  {
    id: 'entry-aug17',
    date: '17 Aug',
    weekday: 'Sunday',
    mood: 3,
    moodLabel: 'Okay',
    urge: 5,
    triggers: ['Watching sport', 'Boredom'],
    excerpt: 'Watched the match and felt the pull strongly. Turned off the TV and called my brother instead.',
  },
  {
    id: 'entry-aug16',
    date: '16 Aug',
    weekday: 'Saturday',
    mood: 4,
    moodLabel: 'Good',
    urge: 4,
    triggers: ['Social pressure'],
    excerpt: 'Friends were talking about a big race. Felt some pressure but said I was taking a break from that stuff.',
  },
  {
    id: 'entry-aug15',
    date: '15 Aug',
    weekday: 'Friday',
    mood: 5,
    moodLabel: 'Great',
    urge: 2,
    triggers: [],
    excerpt: 'Best day in a while. Went for a run in the morning. No urges at all. Feeling strong.',
  },
];

function moodBadgeVariant(mood: number) {
  if (mood >= 4) return 'success';
  if (mood === 3) return 'neutral';
  return 'warning';
}

function urgeBadgeVariant(urge: number) {
  if (urge <= 3) return 'success';
  if (urge <= 6) return 'warning';
  return 'danger';
}

export default function DiaryFeed() {
  const [openId, setOpenId] = useState<string | null>(null);

  return (
    <div className="bg-card rounded-xl border border-border flex flex-col h-full">
      <div className="px-5 py-4 border-b border-border">
        <h3 className="text-base font-700 text-foreground">Recent Entries</h3>
        <p className="text-xs text-muted-foreground mt-0.5">Your last few check-ins</p>
      </div>
      <div className="flex-1 overflow-y-auto divide-y divide-border">
        {pastEntries.map((entry) => {
          const isOpen = openId === entry.id;
          return (
            <div key={entry.id} className="transition-colors hover:bg-muted/30">
              <button
                className="w-full text-left px-5 py-3.5 flex items-center gap-3"
                onClick={() => setOpenId(isOpen ? null : entry.id)}
                aria-expanded={isOpen}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-700 text-foreground">{entry.weekday}</span>
                    <span className="text-xs text-muted-foreground">{entry.date}</span>
                    <Badge variant={moodBadgeVariant(entry.mood)}>
                      Mood {entry.mood}/5
                    </Badge>
                    <Badge variant={urgeBadgeVariant(entry.urge)}>
                      Urge {entry.urge}/10
                    </Badge>
                  </div>
                  {!isOpen && (
                    <p className="text-xs text-muted-foreground mt-1 truncate pr-4">
                      {entry.excerpt}
                    </p>
                  )}
                </div>
                {isOpen ? (
                  <ChevronUp size={14} className="flex-shrink-0 text-muted-foreground" />
                ) : (
                  <ChevronDown size={14} className="flex-shrink-0 text-muted-foreground" />
                )}
              </button>
              {isOpen && (
                <div className="px-5 pb-4 entry-accordion-open">
                  {entry.triggers.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {entry.triggers.map((t) => (
                        <span
                          key={`${entry.id}-trigger-${t}`}
                          className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                  <p className="text-sm text-foreground leading-relaxed">{entry.excerpt}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="px-5 py-3 border-t border-border">
        <button className="text-xs font-600 text-primary hover:underline">
          View all entries →
        </button>
      </div>
    </div>
  );
}